import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { ASSIGN_TECHNICIAN_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  EligibleTechnician,
  ListEligibleTechniciansParams,
  ListEligibleTechniciansResult,
} from '../types/assignTechnicianType';
import serviceRequestEndpoints from './serviceRequestEndpoints';

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (typeof obj.error === 'string' && obj.error.trim().length > 0) {
      return obj.error;
    }
    if (typeof obj.message === 'string' && obj.message.trim().length > 0) {
      return obj.message;
    }
  }
  return ASSIGN_TECHNICIAN_ERROR_MESSAGES.ELIGIBLE_FETCH_ERROR;
};

export const listEligibleTechnicians = async ({
  serviceRequestId,
  startDate,
  endDate,
  startTime,
  endTime,
  page,
  limit,
  search,
  excludeRejected,
  assigneeType = 'technician',
}: ListEligibleTechniciansParams): Promise<ListEligibleTechniciansResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  const isAdmin = assigneeType === 'admin';

  const params = new URLSearchParams();
  // Canonical /eligible-assignees requires an explicit `type`; the deprecated
  // /eligible-technicians alias defaults it, so we only send it for admins to
  // keep the technician request byte-identical to the previous implementation.
  if (isAdmin) {
    params.append('type', 'admin');
  }
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (startTime) {
    params.append('start_time', startTime);
  }
  if (endTime) {
    params.append('end_time', endTime);
  }
  if (typeof page === 'number') {
    params.append('page', String(page));
  }
  if (typeof limit === 'number') {
    params.append('limit', String(limit));
  }
  if (search?.trim()) {
    params.append('search', search.trim());
  }
  // `exclude_rejected` is technician-only (admins have no per-SR rejection).
  if (!isAdmin && excludeRejected === false) {
    params.append('exclude_rejected', 'false');
  }

  const basePath = isAdmin
    ? serviceRequestEndpoints.ELIGIBLE_ASSIGNEES(trimmedId)
    : serviceRequestEndpoints.ELIGIBLE_TECHNICIANS(trimmedId);
  const endpoint = `${basePath}?${params.toString()}`;

  try {
    const result = await apiService.getAPI(endpoint);

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        data?: Array<EligibleTechnician & { business_id?: string }>;
      };
      const rawList = Array.isArray(payload.data) ? payload.data : [];
      // The admin list returns the unified `{ id, business_id, name }` shape.
      // Normalize it into EligibleTechnician (business_id → technician_id) so the
      // shared dropdown renders both kinds through a single code path.
      const list: EligibleTechnician[] = isAdmin
        ? rawList.map((item) => ({
            id: item.id,
            technician_id: item.technician_id ?? item.business_id ?? '',
            name: item.name,
            phone_number: item.phone_number ?? null,
            profile: item.profile ?? null,
          }))
        : rawList;
      // If we asked for a specific page size, treat a full page as a hint
      // that another page may exist; a short page means we've reached the end.
      const hasNextPage =
        typeof limit === 'number' ? list.length >= limit : false;
      return {
        success: true,
        data: list,
        hasNextPage,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
